import { useContext } from 'react';
import type { StateCreator, StoreApi } from 'zustand';
import { useStore } from 'zustand';
import { devtools } from 'zustand/middleware';

import { createGenericStoreWithDevtools } from 'Stores/store';
import { isDev } from 'Utilities/helpers';

import { setMakeAndModel } from './InsightsStore.helpers';
import type { InsightsStore, InsightStoreInitialValues } from './InsightsStore.types';
import { InsightsContext } from './InsightsStoreContext';

export const createInsightsStore = (
	initialValues: InsightStoreInitialValues,
): StateCreator<InsightsStore, [], [['zustand/devtools', never]]> =>
	devtools<InsightsStore>(
		(set) => ({
			make: initialValues.make,
			model: initialValues.model,
			setMakeAndModel: setMakeAndModel(set),
		}),
		{
			enabled: Boolean(isDev),
			name: 'InsightsStore',
		},
	);

export const createInsightsStoreWithInitial = ({ make, model }: InsightStoreInitialValues): StoreApi<InsightsStore> => {
	return createGenericStoreWithDevtools(createInsightsStore({ make, model }));
};

export const useInsightsStore = <T>(selector: (state: InsightsStore) => T, equalityFn?: () => boolean): T => {
	const store = useContext(InsightsContext);
	if (!store) {
		throw new Error('Missing InsightsStoreContext.Provider in the tree');
	}
	return useStore(store, selector, equalityFn);
};
